"use client"

/**
 * AudioScriptPanel
 * Sheet that shows community scripts created for a specific audiochan track.
 * Opened via the "Find Scripts" button on an AudioCard.
 */

import { useEffect, useState } from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Loader2, Star, Download, Play, ShoppingCart } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface AudioScriptResult {
  id: string
  title: string
  description?: string
  creator_username?: string
  creator_avatar?: string
  price_cents: number
  avg_rating: number
  review_count: number
  purchase_count: number
  tags: string[]
  script_duration_ms: number
  content_type: string
}

interface AudioScriptPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  audioSourceId: string
  audioTitle: string
}

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${sec.toString().padStart(2, "0")}`
}

export function AudioScriptPanel({ open, onOpenChange, audioSourceId, audioTitle }: AudioScriptPanelProps) {
  const t = useTranslations("audioScriptPanel")
  const [scripts, setScripts] = useState<AudioScriptResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !audioSourceId) return
    setLoading(true)
    setError(null)

    fetch(`/api/marketplace/scripts?audioSourceId=${encodeURIComponent(audioSourceId)}&sortBy=popular&limit=20`)
      .then((r) => r.json())
      .then((data) => {
        setScripts(data.scripts || data || [])
        setLoading(false)
      })
      .catch(() => {
        setError(t("failedToLoadScripts"))
        setLoading(false)
      })
  }, [open, audioSourceId])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[70vh] bg-[#13131a] border-t border-white/10">
        <SheetHeader className="pb-3 border-b border-white/[0.06]">
          <SheetTitle className="text-white text-sm font-semibold">
            {t("scriptsFor")} <span className="text-purple-300">{audioTitle}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="overflow-y-auto h-full pb-8 pt-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-purple-400" />
            </div>
          )}

          {error && (
            <p className="text-center text-white/40 text-sm py-8">{error}</p>
          )}

          {!loading && !error && scripts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-white/40 text-sm">{t("noScriptsFoundForThisTrackYet")}</p>
              <p className="text-white/25 text-xs mt-1">{t("beTheFirstToCreateOne")}</p>
            </div>
          )}

          {!loading && scripts.length > 0 && (
            <div className="flex flex-col gap-2 px-1">
              {scripts.map((script) => (
                <div
                  key={script.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.04] border border-white/[0.07] hover:border-purple-700/40 transition-colors"
                >
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white/90 truncate">{script.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {script.creator_username && (
                        <span className="text-[11px] text-white/40">{script.creator_username}</span>
                      )}
                      {script.avg_rating > 0 && (
                        <span className="flex items-center gap-0.5 text-[11px] text-yellow-400">
                          <Star className="h-2.5 w-2.5 fill-current" />
                          {script.avg_rating.toFixed(1)}
                        </span>
                      )}
                      {script.script_duration_ms > 0 && (
                        <span className="text-[11px] text-white/30">{formatDuration(script.script_duration_ms)}</span>
                      )}
                    </div>
                    {script.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {script.tags.slice(0, 4).map((tag) => (
                          <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/40 border border-white/[0.07]">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Price + action */}
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {script.price_cents === 0 ? (
                      <Badge className="bg-emerald-900/50 text-emerald-300 border-emerald-700/40 text-[10px]">{t("free")}</Badge>
                    ) : (
                      <Badge className="bg-purple-900/50 text-purple-200 border-purple-700/40 text-[10px]">
                        ${(script.price_cents / 100).toFixed(2)}
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[11px] text-white/60 hover:text-white hover:bg-purple-900/30"
                    >
                      {script.price_cents === 0 ? (
                        <><Play className="h-3 w-3 mr-1" />{t("use")}</>
                      ) : (
                        <><ShoppingCart className="h-3 w-3 mr-1" />{t("buy")}</>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
